import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';

/* Styled Components */
import {
  FormSection,
  PageSubtitle,
  PageFooter,
} from 'styles/signup/MultiScreenSignupPage';
import { FormElementTitle } from 'styles/common/FormElement';

/* Child Components */
import Button, { BUTTON_COLOR_KEYS } from 'components/common/Button';
import PlaceAutocompleteTextInput from 'components/signup/autocomplete/PlaceAutocompleteTextInput';
import BlueTextBox from 'components/signup/multiscreen/BlueTextBox';
import SectionHeader from 'components/signup/multiscreen/SectionHeader';

/* Icons */
import MapPinIcon from 'icons/MapPinIcon';

/* Actions */
import {
  submitSignupStep as submitSignupStepAction,
  setCurrentSignupStep as setCurrentSignupStepAction,
  setStoreName as setStoreNameAction,
  setAutocompleteSessionToken as setAutocompleteSessionTokenAction,
  setAutocompleteAttributions as setAutocompleteAttributionsAction,
  setJustDidAutoComplete as setJustDidAutoCompleteAction,
  setPlaceDetailsResult as setPlaceDetailsResultAction,
  setIsStoreNameValidated as setIsStoreNameValidatedAction,
  setIsOpenOnDay as setIsOpenOnDayAction,
  setStoreHours as setStoreHoursAction,
  setIsSecondHoursShiftVisible as setIsSecondHoursShiftVisibleAction,
  setStoreType as setStoreTypeAction,
  clearErrorMessage as clearErrorAction,
  setErrorMessage as setErrorMessageAction,
} from 'app/signupSlice';

/* Selectors */
import { getLocale, getCountryCode } from 'app/appSlice';
import {
  getStoreName,
  getAutocompleteSessionToken,
  getAutocompleteAttributions,
  getPlaceDetailsResult,
  isStoreNameValidated,
  getGooglePlacesOpeningHours,
  getStoreType,
  getErrorMessage,
} from 'app/signupSlice';

/* Utils */
import { i18n } from 'utils/i18n';
import {
  SUPPORTED_HOUR_SETS,
  MinuteRoundDirection,
  googleDayIndexToBlueDayIndex,
  googleTimeToSeconds,
  secondsToTime,
} from 'utils/StoreHours';

/* Constants */
import {
  SIGNUP_SCREEN,
  STORE_TYPE,
  AUTOCOMPLETE_DETAILS_FIELDS,
} from 'constants/SignupConstants';

/* Type */
import type { AppState, AppDispatch } from 'app/store';
import type { PlaceDetailsResultType } from 'types/GooglePlacesTypes';
import type { StoreHours } from 'types/MultiScreenSignupTypes';
import type { OptionsType as SubmitSignupStepOptionsType } from 'services/SubmitSignupStepService';

const mapStateToProps = (state: AppState) => ({
  locale: getLocale(state),
  userCountryCode: getCountryCode(state),
  autocompleteSessionToken: getAutocompleteSessionToken(state),
  autocompleteAttributions: getAutocompleteAttributions(state),
  placeDetailsResult: getPlaceDetailsResult(state),
  storeNameValidated: isStoreNameValidated(state),
  storeName: getStoreName(state),
  googlePlacesHours: getGooglePlacesOpeningHours(state),
  storeType: getStoreType(state),
  errorMessage: getErrorMessage(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  submitSignupStep: (args: {
    signupStep: Values<typeof SIGNUP_SCREEN>;
    nextSignupStep: Values<typeof SIGNUP_SCREEN>;
    options: SubmitSignupStepOptionsType;
  }) => dispatch(submitSignupStepAction(args)),
  setCurrentSignupStep: (signupStep: Values<typeof SIGNUP_SCREEN>) =>
    dispatch(setCurrentSignupStepAction(signupStep)),
  clearError: () => dispatch(clearErrorAction()),
  setErrorMessage: (errorMessage: string) =>
    dispatch(setErrorMessageAction(errorMessage)),
  setAutocompleteSessionToken: (autocompleteSessionToken: string) =>
    dispatch(setAutocompleteSessionTokenAction(autocompleteSessionToken)),
  setAutocompleteAttributions: (autocompleteAttributions: Array<string>) =>
    dispatch(setAutocompleteAttributionsAction(autocompleteAttributions)),
  setJustDidAutoComplete: (justDidAutoComplete: boolean) =>
    dispatch(setJustDidAutoCompleteAction(justDidAutoComplete)),
  setPlaceDetailsResult: (
    placeDetailsResult: PlaceDetailsResultType | undefined,
  ) => dispatch(setPlaceDetailsResultAction(placeDetailsResult)),
  setIsStoreNameValidated: (isValidated: boolean) =>
    dispatch(setIsStoreNameValidatedAction(isValidated)),
  setStoreName: (storeName: string) => dispatch(setStoreNameAction(storeName)),
  setIsOpenOnDay: (dayIndex: number, isOpenOnDay: boolean) =>
    dispatch(setIsOpenOnDayAction({ dayIndex, isOpenOnDay })),
  setStoreHours: (storeHours: StoreHours) =>
    dispatch(setStoreHoursAction(storeHours)),
  setIsSecondHoursShiftVisible: (isVisible: boolean) =>
    dispatch(setIsSecondHoursShiftVisibleAction(isVisible)),
  setStoreType: (storeType: Values<typeof STORE_TYPE>) =>
    dispatch(setStoreTypeAction(storeType)),
});

type PropsType = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const StoreNamePage = ({
  submitSignupStep,
  setCurrentSignupStep,
  errorMessage,
  clearError,
  setErrorMessage,
  locale,
  userCountryCode,
  autocompleteSessionToken,
  setAutocompleteSessionToken,
  autocompleteAttributions,
  setAutocompleteAttributions,
  placeDetailsResult,
  setJustDidAutoComplete,
  setPlaceDetailsResult,
  storeNameValidated,
  setIsStoreNameValidated,
  storeName,
  setStoreName,
  googlePlacesHours,
  setIsOpenOnDay,
  setStoreHours,
  setIsSecondHoursShiftVisible,
  storeType,
  setStoreType,
}: PropsType) => {
  const [shouldDisplayStoreDetails, setShouldDisplayStoreDetails] =
    useState<boolean>(false);
  const [formattedStoreAddress, setFormattedStoreAddress] =
    useState<string>('');
  const [originalStoreType] = useState<Values<typeof STORE_TYPE>>(
    storeType || STORE_TYPE.HOME,
  );

  const resetAutocompleteSessionToken = () => {
    setAutocompleteSessionToken(uuid());
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(resetAutocompleteSessionToken, []);

  const onInputChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    clearError();
    const { value } = event.currentTarget;
    setStoreName(value);
    setIsStoreNameValidated(false);
    setPlaceDetailsResult(undefined);
    setStoreType(originalStoreType);
  };

  const onAutocompleteDetails = (
    result: PlaceDetailsResultType,
    attributions: Array<string>,
  ) => {
    setShouldDisplayStoreDetails(true);
    setPlaceDetailsResult(result);
    setAutocompleteAttributions(attributions);
    setJustDidAutoComplete(true);

    const { name, types, formatted_address: returnedAddress } = result;

    if (types?.includes('establishment')) {
      setStoreType(STORE_TYPE.RETAIL);
      if (name) {
        setStoreName(name);
        setIsStoreNameValidated(true);
        setFormattedStoreAddress(returnedAddress || '');
      }
    }
  };

  const onFocusChange = (isFocused: boolean) => {
    if (!isFocused && placeDetailsResult) {
      setShouldDisplayStoreDetails(true);
    }
  };

  const loadHoursFromGooglePlaces = () => {
    const isOpenOnDayMap: { [key: number]: boolean } = {};
    const loadedHours: StoreHours = {};
    let secondShiftVisible = false;

    googlePlacesHours?.periods?.forEach(period => {
      const open = period?.open;
      const close = period?.close;
      if (open?.day !== null && open?.day !== undefined) {
        const { day: googleDayIndex } = open;
        const googleTimeStart = open?.time || '0000';
        const googleTimeEnd = close?.time || '2330';
        const startSeconds = googleTimeToSeconds(
          googleTimeStart,
          MinuteRoundDirection.UP,
        );
        let endSeconds = googleTimeToSeconds(
          googleTimeEnd,
          MinuteRoundDirection.DOWN,
        );

        if (endSeconds < startSeconds) {
          endSeconds = googleTimeToSeconds('2330');
        }
        const dayIndex = googleDayIndexToBlueDayIndex(googleDayIndex);
        const hoursForDay = loadedHours[dayIndex] || {};
        const setOfHoursIndices = Object.keys(hoursForDay).map(x =>
          parseInt(x, 10),
        );
        const nextSetOfHoursIndex =
          setOfHoursIndices.length > 0 ? Math.max(...setOfHoursIndices) + 1 : 0;
        if (nextSetOfHoursIndex > 0) {
          secondShiftVisible = true;
        }
        if (nextSetOfHoursIndex < SUPPORTED_HOUR_SETS) {
          const startTime = secondsToTime(startSeconds);
          const endTime = secondsToTime(endSeconds);
          hoursForDay[nextSetOfHoursIndex] = {
            start: startTime,
            end: endTime,
          };
          loadedHours[dayIndex] = hoursForDay;
          isOpenOnDayMap[dayIndex] = true;
        }
      }
    });

    Object.keys(isOpenOnDayMap).forEach(dayIndex => {
      setIsOpenOnDay(parseInt(dayIndex, 10), true);
    });
    setStoreHours(loadedHours);
    setIsSecondHoursShiftVisible(secondShiftVisible);
  };

  const trimmedStoreName = storeName?.trim();
  const onSubmit = () => {
    if (!trimmedStoreName) {
      setErrorMessage(i18n("Please enter your store's name"));
      return;
    }
    const hasAutocompletedHours = Boolean(googlePlacesHours);
    if (hasAutocompletedHours) {
      loadHoursFromGooglePlaces();
    }
    submitSignupStep({
      signupStep: SIGNUP_SCREEN.STORE_NAME,
      nextSignupStep: SIGNUP_SCREEN.STORE_ADDRESS,
      options: {
        storeName,
        placeDetailsResult,
        autocompleteAttributions,
        storeNameValidated,
      },
    });
  };
  const showError = Boolean(errorMessage);

  return (
    <>
      <SectionHeader
        title={i18n('What is your store called?')}
        onBackClicked={() => {
          clearError();
          setCurrentSignupStep(SIGNUP_SCREEN.STORE_TYPE);
        }}
      />
      <FormSection stretch>
        <FormElementTitle bold>{i18n('Store name')}</FormElementTitle>
        {shouldDisplayStoreDetails ? (
          <>
            <BlueTextBox
              onClick={() => {
                setShouldDisplayStoreDetails(false);
              }}
              title={storeName}
              description={formattedStoreAddress}
              icon={<MapPinIcon />}
              margin="0 0 8px"
            />
            <PageSubtitle>
              {i18n('You’ll be able to edit your address on the next screen')}
            </PageSubtitle>
          </>
        ) : (
          <>
            <PlaceAutocompleteTextInput
              value={storeName}
              stretch
              margin="0 0 8px"
              onChange={onInputChange}
              detailsFields={AUTOCOMPLETE_DETAILS_FIELDS}
              country={userCountryCode}
              language={locale}
              types={['establishment']}
              sessionToken={autocompleteSessionToken}
              resetSessionToken={resetAutocompleteSessionToken}
              onDetails={onAutocompleteDetails}
              onFocusChange={onFocusChange}
              errorMessage={i18n('Required')}
              isErrorShown={!trimmedStoreName && showError}
              shouldShowList
              focus
            />
            <PageSubtitle>
              {i18n('Search for your store name or add it to our list')}
            </PageSubtitle>
          </>
        )}
      </FormSection>

      <PageFooter>
        <Button
          onClick={onSubmit}
          color={BUTTON_COLOR_KEYS.DARK_COBALT}
          stretch
          bold
          overrideStyle={{
            margin: '24px 0',
          }}
        >
          {i18n('Continue')}
        </Button>
      </PageFooter>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(StoreNamePage);
